import React from 'react';
import { Helmet } from 'react-helmet';

const DEFAULT_SITE_URL = 'https://skylinevfx.com';
const DEFAULT_OG_IMAGE_PATH = '/assets/portfolio/Concept art/32Ford_coupe.png';

const defaultMeta = {
  title: 'Skyline VFX — Digital Matte Painting & Concept Art',
  description: 'Portfolio of Lee Munday — Digital Matte Painter and Concept Artist for film and television VFX.',
  ogType: 'website',
};

function getSiteUrl() {
  // Prefer build-time env so canonical URLs are stable.
  // Fallback to window origin (dev) and then a safe default.
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL) {
    return String(import.meta.env.VITE_SITE_URL).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return DEFAULT_SITE_URL;
}

function getCanonicalUrl() {
  const base = getSiteUrl();
  if (typeof window === 'undefined') return `${base}/`;
  const path = window.location.pathname || '/';
  return `${base}${path}`;
}

function getOgImageUrl() {
  const base = getSiteUrl();
  return `${base}${encodeURI(DEFAULT_OG_IMAGE_PATH)}`;
}

const GOOGLE_SITE_VERIFICATION = import.meta.env?.VITE_GOOGLE_SITE_VERIFICATION;

// Basic structured data
function getSchemas() {
  const base = getSiteUrl();

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Skyline VFX',
    url: base,
    email: 'lmatthews@skylinevfx.com',
  };

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Lee Munday',
    jobTitle: 'Digital Matte Painter and Concept Artist',
    url: `${base}/about`,
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Skyline VFX',
    url: base,
  };

  return [organizationSchema, personSchema, websiteSchema];
}

export default function Seo({ title, description, og = {}, schema = null }) {
  const meta = {
    ...defaultMeta,
    title: title || defaultMeta.title,
    description: description || defaultMeta.description,
    ...og,
  };
  const ogImage = meta.image || getOgImageUrl();

  const schemas = [...getSchemas()];
  if (schema) schemas.push(schema);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {GOOGLE_SITE_VERIFICATION ? (
        <meta name="google-site-verification" content={GOOGLE_SITE_VERIFICATION} />
      ) : null}

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content={meta.ogType} />
      <meta property="og:url" content={getCanonicalUrl()} />
      {/* If you want a dedicated OG image, add one to /public and reference it here. */}
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Skyline VFX" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />

      <link rel="canonical" href={getCanonicalUrl()} />

      {/* Structured Data (JSON-LD) */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

